import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router';

import { Button } from '#/modules/commonComponents/button';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';

const remarkFormSchema = z.object({
  remark: z.string().max(500, '備註最多 500 字').optional().nullable(),
});

const REMARK_FIELD_LABELS = {
  remark: '備註',
};

/**
 * 單據備註專頁共用表單（入庫／轉倉／調整皆可接）。
 * 不綁定特定 API；由父層／hook 傳 onSave。
 */
const DocumentRemarkEditForm = ({
  initialRemark = '',
  cancelTo,
  isPending = false,
  onSave,
}) => {
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(remarkFormSchema),
    defaultValues: { remark: initialRemark ?? '' },
  });

  useEffect(() => {
    resetValidationSummary();
    reset({ remark: initialRemark ?? '' });
  }, [initialRemark, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    onSave?.(values.remark ?? '');
  }

  return (
    <form
      className="sheet create_edit create_edit_form create_edit_form--page-scroll"
      onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
      noValidate
    >
      <div className="create_edit_form__body">
        <FormValidationSummary
          show={isSubmitAttempted}
          errors={errors}
          fieldLabels={REMARK_FIELD_LABELS}
          customMessages={customMessages}
        />
        <FloatingTextarea
          id="remark"
          label="備註"
          register={register}
          errors={errors}
          rows={4}
        />
      </div>
      <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>取消</Link>
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? '儲存中…' : '儲存備註'}
        </Button>
      </div>
    </form>
  );
};

DocumentRemarkEditForm.propTypes = {
  initialRemark: PropTypes.string,
  cancelTo: PropTypes.string.isRequired,
  isPending: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default DocumentRemarkEditForm;
